import { useState } from "react";
import styled from "styled-components";
import { Users, Maximize, Wifi, ChevronLeft, ChevronRight } from "lucide-react";
import { usePage } from "@inertiajs/react";
import Lightbox from "./Lightbox";
import {
  Container,
  SectionHeader,
  Subtitle,
  Title,
  Description,
} from "./Section";
import { useLandingLanguage } from "../../../contexts/landing-language";

const RoomsSection = styled.section`
  padding: 120px 24px;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 80px 24px;
  }
`;

const RoomsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RoomCard = styled.div`
  background: #ffffff;
  overflow: hidden;
  border: 1px solid #e5e1dc;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-12px);
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.12);
    border-color: transparent;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  height: 220px;
  overflow: hidden;
  cursor: pointer;

  &:hover > .carousel-nav {
    opacity: 1;
  }
`;

const CarouselImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: ${(props) =>
    props.$imageUrl
      ? `url(${props.$imageUrl})`
      : "linear-gradient(135deg, #e5e1dc 0%, #d4cfc7 100%)"};
  background-size: cover;
  background-position: center;
  transition: background-image 0.4s ease;
`;

const CarouselNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.$side === "left" ? "left: 8px;" : "right: 8px;")}
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: #ffffff;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s, background 0.2s;
  z-index: 2;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.55);
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 10px;
  letter-spacing: 0.5px;
  z-index: 2;
`;

const RoomContent = styled.div`
  padding: 32px;
`;

const RoomTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
  letter-spacing: 0.3px;
`;

const RoomDesc = styled.p`
  font-size: 14px;
  color: #8b7355;
  line-height: 1.8;
  margin-bottom: 24px;
  font-weight: 400;
`;

const RoomFeatures = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;

  svg {
    color: #c9a86c;
    width: 16px;
    height: 16px;
  }
`;

const OtaCta = styled.a`
  display: block;
  width: 100%;
  text-align: center;
  background: transparent;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 14px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 2px;
  text-transform: uppercase;
  text-decoration: none;

  &:hover {
    background: #1a1a1a;
    color: #faf8f5;
  }
`;

const RoomImageCarousel = ({ images, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasMultiple = images.length > 1;

  const prev = (e) => {
    e.stopPropagation();
    setCurrentIndex((i) => (i - 1 + images.length) % images.length);
  };

  const next = (e) => {
    e.stopPropagation();
    setCurrentIndex((i) => (i + 1) % images.length);
  };

  return (
    <CarouselWrapper onClick={() => onImageClick(currentIndex)}>
      <CarouselImage $imageUrl={images[currentIndex]} />
      {hasMultiple && (
        <>
          <CarouselNav className="carousel-nav" $side="left" onClick={prev}>
            <ChevronLeft size={18} />
          </CarouselNav>
          <CarouselNav className="carousel-nav" $side="right" onClick={next}>
            <ChevronRight size={18} />
          </CarouselNav>
          <ImageCounter>
            {currentIndex + 1} / {images.length}
          </ImageCounter>
        </>
      )}
    </CarouselWrapper>
  );
};

const Rooms = () => {
  const { room_types } = usePage().props;
  const { t } = useLandingLanguage();
  const [lightbox, setLightbox] = useState(null);

  const scrollToBooking = () => {
    const element = document.getElementById("booking");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <RoomsSection id="rooms">
      <Container>
        <SectionHeader>
          <Subtitle>{t.rooms.subtitle}</Subtitle>
          <Title>{t.rooms.title}</Title>
          <Description>{t.rooms.description}</Description>
        </SectionHeader>

        <RoomsGrid>
          {(room_types || []).map((room, index) => (
            <RoomCard key={index}>
              <RoomImageCarousel
                images={room.image_urls || []}
                onImageClick={(imgIndex) =>
                  setLightbox({ images: room.image_urls, initialIndex: imgIndex })
                }
              />
              <RoomContent>
                <RoomTitle>{room.name}</RoomTitle>
                <RoomDesc>{room.short_desc}</RoomDesc>
                <RoomFeatures>
                  {room.number_of_rooms && (
                    <Feature>
                      <Users size={18} />
                      <span>{room.number_of_rooms} rooms</span>
                    </Feature>
                  )}
                  {room.luas_m2 && (
                    <Feature>
                      <Maximize size={18} />
                      <span>{room.luas_m2} m²</span>
                    </Feature>
                  )}
                  <Feature>
                    <Wifi size={18} />
                    <span>Free WiFi</span>
                  </Feature>
                </RoomFeatures>
                <OtaCta onClick={scrollToBooking} href="#booking">
                  {t.rooms.checkRates}
                </OtaCta>
              </RoomContent>
            </RoomCard>
          ))}
        </RoomsGrid>
      </Container>

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          initialIndex={lightbox.initialIndex}
          onClose={() => setLightbox(null)}
        />
      )}
    </RoomsSection>
  );
};

export default Rooms;
